import {
  Sidebar as BaseSidebar,
  DashboardIcon,
  DeputateIcon,
  LockIcon,
  ProfileIcon,
  SettingsIcon,
  SidebarContentProps,
  SidebarItemProps,
  SubstitutionsIcon,
  TimetableIcon,
} from '@bp/ui-components';
import classNames from 'classnames';
import { memo, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { AppLayoutContext } from '../../contexts/AppLayoutContextProvider';
import { usePermissionChecker } from '../../hooks/usePermissionChecker';

import styles from './Sidebar.module.scss';

export const Sidebar = memo(() => {
  const { t } = useTranslation();
  const location = useLocation();
  const { sidebarIsFixed, setSidebarIsFixed, sidebarIsOpen, setSidebarIsOpen } = useContext(AppLayoutContext);

  const permissions = usePermissionChecker();

  const classes = classNames(styles.sidebar, 'has-scrollbar');

  const sidebarContent: SidebarContentProps[] = useRef([
    {
      items: [
        {
          icon: <DashboardIcon />,
          link: '',
          title: 'Dashboard',
        },
        ...(permissions?.canReadDeputate()
          ? [
              {
                icon: <DeputateIcon />,
                link: t('routes.deputate.slug'),
                title: t('routes.deputate.title'),
                sublist: [
                  {
                    title: t('routes.setup.title'),
                    link: t('routes.setup.slug'),
                  },
                  {
                    title: t('routes.plan.title'),
                    link: t('routes.plan.slug'),
                  },
                  {
                    title: t('routes.evaluate.title'),
                    link: t('routes.evaluate.slug'),
                  },
                ],
              },
            ]
          : []),
        ...(permissions?.canReadTimetable()
          ? [
              {
                icon: <TimetableIcon />,
                link: t('routes.timetable.slug'),
                title: t('timetable.title', { count: 2 }),
                sublist: [
                  {
                    title: t('routes.setup.title'),
                    link: t('routes.setup.slug'),
                  },
                  {
                    title: t('routes.plan.title'),
                    link: t('routes.plan.slug'),
                  },
                  {
                    title: t('routes.evaluate.title'),
                    link: t('routes.evaluate.slug'),
                  },
                ],
              },
            ]
          : []),
        ...(permissions?.canManageSubstitution()
          ? [
              {
                icon: <SubstitutionsIcon />,
                link: t('routes.substitutions.slug'),
                title: t('substitutions.title', { count: 2 }),
                sublist: [
                  {
                    title: t('routes.setup.title'),
                    link: t('routes.setup.slug'),
                  },
                  {
                    title: t('routes.plan.title'),
                    link: t('routes.plan.slug'),
                  },
                  {
                    title: t('routes.evaluate.title'),
                    link: t('routes.evaluate.slug'),
                  },
                ],
              },
            ]
          : []),
      ],
    },
  ]).current as SidebarContentProps[];

  const sidebarFooterItems: SidebarItemProps[] = useRef([
    ...(permissions?.canManageSettings()
      ? [
          {
            icon: <SettingsIcon />,
            link: t('routes.settings.slug'),
            title: t('settings.title', { count: 2 }),
          },
        ]
      : []),
    ...(permissions?.canManageAdministration()
      ? [
          {
            icon: <LockIcon />,
            link: t('routes.admin.slug'),
            title: t('routes.admin.title'),
          },
        ]
      : []),
    {
      icon: <ProfileIcon />,
      link: t('routes.profile.slug'),
      title: t('profile.title', { count: 1 }),
    },
  ]).current as SidebarItemProps[];

  const [sidebarToggleState, setSidebarToggleState] = useState(false);

  useEffect(() => {
    if (sidebarIsFixed !== sidebarToggleState) {
      setSidebarToggleState(sidebarIsFixed);
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 300);
    }
  }, [sidebarIsFixed, sidebarToggleState]);

  return (
    <BaseSidebar
      isDesktop={true}
      sidebarContent={sidebarContent}
      sidebarFooterItems={sidebarFooterItems}
      isExpanded={sidebarIsOpen}
      setIsExpanded={setSidebarIsOpen}
      isFixed={sidebarIsFixed}
      setIsFixed={setSidebarIsFixed}
      logoType={'planung'}
      currentLocationPath={location.pathname}
      className={classes}
    />
  );
});

Sidebar.displayName = 'Sidebar';
